import { useState } from "react";
import {
  Alert, Box, Chip, IconButton, MenuItem, Paper, Stack, TextField, Tooltip,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import ReplayIcon from "@mui/icons-material/Replay";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gstApi, type EWayBillRow, type EWayBillStatus } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { formatDateTime } from "@/shared/datetime";

const STATUSES: ("" | EWayBillStatus)[] = ["", "Pending", "Generated", "Failed", "Expired", "NotRequired"];

/** S13 — E-Way bill register (inter-state shipments). Failed rows re-queueable. */
export function EWayBillsPage() {
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("integration.gst.einvoice.generate"));
  const [status, setStatus] = useState<"" | EWayBillStatus>("");
  const [q, setQ] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const query = useQuery({
    queryKey: ["ewaybills", status, q, pagination.page, pagination.pageSize],
    queryFn: () => gstApi.listEWayBills({
      status: status || undefined, q: q || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const retryMut = useMutation({
    mutationFn: (id: string) => gstApi.retryEWayBill(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ewaybills"] }),
  });

  const columns: GridColDef<EWayBillRow>[] = [
    { field: "transferDocumentNo", headerName: "Transfer #", width: 170 },
    { field: "status", headerName: "Status", width: 140,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <StatusPill label={p.value as string} />
          {p.row.manuallyEntered && (
            <Tooltip title="Manually entered"><Chip size="small" color="info" label="MAN" /></Tooltip>
          )}
        </Stack>
      ) },
    { field: "ewbNo", headerName: "EWB No", width: 180 },
    { field: "generatedAt", headerName: "Generated", width: 170,
      valueFormatter: (v) => v ? formatDateTime(v as string) : "—" },
    { field: "validUntil", headerName: "Valid until", width: 170,
      valueFormatter: (v) => v ? formatDateTime(v as string) : "—" },
    { field: "attemptCount", headerName: "Tries", width: 70, type: "number" },
    { field: "lastError", headerName: "Last error", flex: 1, minWidth: 200 },
    { field: "actions", headerName: "", width: 60, sortable: false, filterable: false,
      renderCell: (p) => (canManage && p.row.status === "Failed") ? (
        <IconButton size="small" disabled={retryMut.isPending}
          onClick={() => retryMut.mutate(p.row.id)}>
          <ReplayIcon fontSize="small" />
        </IconButton>
      ) : null },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Integrations"]} title="E-Way bills" />
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} size="small"
            onChange={(e) => setStatus(e.target.value as "" | EWayBillStatus)} sx={{ minWidth: 160 }}>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
          <TextField label="Search transfer # / EWB" value={q} size="small"
            onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 240 }} />
        </Stack>
        {(query.isError || retryMut.isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error ?? retryMut.error)}</Alert>
        )}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
          />
        </Box>
      </Paper>
    </Box>
  );
}
